import { useLocation, useNavigate } from "@tanstack/react-router";
import { useState, type CSSProperties, type ReactNode } from "react";
import { useAuthStore } from "@/lib/auth-store";
import { logout as logoutApi } from "@/shared/api/auth.api";
import { toastInfo } from "@/lib/toast";
import { AppShellHeader } from "./AppShellHeader";
import { AppShellNavbar } from "./AppShellNavbar";

const HEADER_H = 56; // px · igual a `header={{ height: 56 }}` del AppShell de Mantine
const NAVBAR_EXPANDED = 248; // px
const NAVBAR_RAIL = 72; // px
const SHELL_PADDING = 16; // px · `padding="md"` del AppShell de Mantine

/**
 * AppShell del portal · reemplazo de `components/layout/app-shell.tsx`
 * (Mantine `<AppShell>`). Sin el componente de Mantine hay que reponer a
 * mano las 3 zonas (header fijo / navbar fijo / main) y las CSS vars que
 * otras piezas ya migradas consumen (`PASO_PAGE_SHELL`, sección Etapa 4
 * de `SolicitudStepper`) — por eso se exponen como `--shell-header-h` /
 * `--shell-navbar-w` / `--shell-padding` en el contenedor raíz, con los
 * MISMOS nombres semánticos que las `--app-shell-*` de Mantine.
 */
export function AppShellMultiva({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopExpanded, setDesktopExpanded] = useState(true);
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const roles = user?.roles ?? [];
  const permisos = user?.permisos ?? [];

  async function handleLogout() {
    const refreshToken = useAuthStore.getState().refreshToken;
    await logoutApi(refreshToken);
    clear();
    toastInfo({ title: "Sesión cerrada", message: "Hasta luego" });
    void navigate({ to: "/login" as never, replace: true });
  }

  const navbarWidth = desktopExpanded ? NAVBAR_EXPANDED : NAVBAR_RAIL;

  return (
    <div
      className="min-h-screen bg-slate-100/70 dark:bg-ink-900"
      style={
        {
          "--shell-header-h": `${HEADER_H}px`,
          "--shell-navbar-w": `${navbarWidth}px`,
          "--shell-padding": `${SHELL_PADDING}px`,
        } as CSSProperties
      }
    >
      <AppShellHeader user={user} mobileNavOpen={mobileOpen} onMobileToggle={() => setMobileOpen((v) => !v)} onLogout={() => void handleLogout()} />

      {/* Backdrop del navbar en mobile (overlay) · clic afuera cierra. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 md:hidden"
          style={{ top: "var(--shell-header-h)", zIndex: 190 }}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <AppShellNavbar
        roles={roles}
        permisos={permisos}
        pathname={pathname}
        mobileOpen={mobileOpen}
        onItemClick={() => setMobileOpen(false)}
        colapsado={!desktopExpanded}
        onToggleColapso={() => setDesktopExpanded((v) => !v)}
      />

      <main
        className="pt-[var(--shell-header-h)] transition-[padding-left] duration-200 ease-out-quint md:pl-[var(--shell-navbar-w)]"
      >
        <div style={{ padding: "var(--shell-padding)" }}>{children}</div>
      </main>
    </div>
  );
}
